import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SubmitForm from "@/pages/about/components/SubmitForm";
import VoicesSection from "@/pages/home/components/VoicesSection";
import usePageMeta from "@/hooks/usePageMeta";
import { faqs } from "@/mocks/faqs";
import { site, stats } from "@/mocks/site";

const principles = [
  {
    icon: "ri-emotion-laugh-line",
    title: "先好笑，再更新",
    desc: "一条内容能不能上架，判断标准只有两个：读起来是否自然、笑点是否站得住。宁可少发，也不凑数。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不指向具体个人",
    desc: "所有内容围绕情绪与生活场景展开，不做人身指向，也不收录未经证实的信息与私人纠纷。",
  },
  {
    icon: "ri-user-heart-line",
    title: "投稿者有署名",
    desc: "来自网友的原创表情包、段子与二创视频，采纳后一定标注作者，好内容值得被看见。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续上新不掉线",
    desc: "每天固定三次更新，热点出现时临时加更。神回复与段子的更新频率高于其他频道。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 网爆吃瓜内容理念与投稿方式",
    description:
      "了解网爆吃瓜的站点定位、内容原则与团队理念：吃瓜娱乐二创聚集地，专注表情包、段子、神回复与梗图，欢迎投稿与内容反馈。",
    keywords: "网爆吃瓜,关于网爆吃瓜,吃瓜二创,表情包投稿,内容原则,联系方式",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于网爆吃瓜",
        description: site.description,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.brand,
        description: site.description,
        email: site.email,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  });

  useEffect(() => {
    if (window.location.hash === "#submit") {
      const target = document.getElementById("submit");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200/80 bg-background-100/70">
          <div className="container-page">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
            <div className="pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-7">
                <span className="section-eyebrow">About Us</span>
                <h1 className="mt-3 text-3xl md:text-5xl text-foreground-950 text-balance">
                  关于 {site.brand}
                </h1>
                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                  {site.brand}是一个吃瓜娱乐二创内容站。我们把散落在各个群聊里的表情包、段子、神回复、梗图和二创视频整理到一起，让每一次刷手机都变成一次轻松的放空。
                </p>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                  这里没有严肃的资讯，也没有夸张的标题。我们只做一件事：把好笑的挑出来，整理好，放在你顺手就能拿到的位置。
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#submit"
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    投稿或联系
                  </a>
                  <Link
                    to="/gengtu"
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    先逛一逛
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <figure className="rounded-lg overflow-hidden border border-background-200 bg-background-50">
                  <img
                    src="https://readdy.ai/api/search-image?query=flat%20vector%20illustration%20of%20a%20cozy%20creative%20studio%20desk%20with%20sketchbooks%2C%20sticker%20sheets%20and%20a%20watermelon%20mug%2C%20warm%20coral%20orange%20golden%20yellow%20and%20cream%20palette%2C%20bold%20rounded%20shapes%2C%20soft%20paper%20texture%2C%20high%20detail%2C%20no%20text&width=900&height=700&seq=wg-about-01&orientation=landscape"
                    alt="网爆吃瓜内容创作台，整理表情包与梗图的工作场景"
                    title="网爆吃瓜 内容创作与整理"
                    className="w-full aspect-[9/7] object-cover object-top"
                  />
                  <figcaption className="px-4 py-3 text-xs text-foreground-500 border-t border-background-200">
                    每天从各个群聊与投稿箱里挑选内容，再统一整理、标注场景。
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page py-14 md:py-20">
          <SectionHeading
            eyebrow="Positioning"
            title="我们给自己的定位"
            desc="不是资讯站，也不是八卦集散地。我们更像一个持续更新的乐子素材库，需要的时候打开，顺手就能存下一两张。"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <article className="p-6 rounded-lg bg-background-50 border border-background-200/80">
              <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                <i className="ri-archive-line"></i>
              </span>
              <h3 className="mt-4 text-base text-foreground-950">一个素材库</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                按频道和场景分类整理，找图不用翻历史记录，打开频道就能直接挑。
              </p>
            </article>
            <article className="p-6 rounded-lg bg-background-50 border border-background-200/80">
              <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-xl">
                <i className="ri-group-line"></i>
              </span>
              <h3 className="mt-4 text-base text-foreground-950">一个二创社区</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                评论区、投稿箱和二创视频频道连在一起，你的脑洞有机会被更多人看到。
              </p>
            </article>
            <article className="p-6 rounded-lg bg-background-50 border border-background-200/80">
              <span className="w-11 h-11 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center text-xl">
                <i className="ri-compass-3-line"></i>
              </span>
              <h3 className="mt-4 text-base text-foreground-950">一个放空角落</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                通勤、排队、午休的碎片时间，进来刷几分钟，笑一笑再继续。
              </p>
            </article>
          </div>
        </section>

        <section className="bg-secondary-50">
          <div className="container-page py-14 md:py-20">
            <SectionHeading
              eyebrow="Principles"
              title="内容原则：四条不妥协"
              desc="这几条写在这里，也写在每一次选题会上。它们决定了什么内容能出现在网爆吃瓜。"
            />
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {principles.map((item, index) => (
                <li key={item.title} className="flex gap-4 p-5 rounded-lg bg-background-50 border border-secondary-200/70">
                  <span
                    className={`w-11 h-11 shrink-0 rounded-md flex items-center justify-center text-xl ${
                      index % 2 === 0 ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-800"
                    }`}
                  >
                    <i className={item.icon}></i>
                  </span>
                  <div>
                    <h3 className="text-base text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="container-page py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {stats.map((item) => (
              <div key={item.label} className="p-6 rounded-lg bg-background-100/80 border border-background-200/80">
                <span className="block text-2xl md:text-3xl font-heading text-primary-600">{item.value}</span>
                <span className="mt-2 block text-sm text-foreground-600">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100/70">
          <div className="container-page py-14 md:py-20">
            <SectionHeading
              eyebrow="Voices"
              title="用户口碑"
              desc="来自长期蹲守的网友与二创投稿者的反馈，也是我们持续更新的动力。"
            />
            <VoicesSection />
          </div>
        </section>

        <section className="container-page py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="FAQ"
                title="常见问题全部解答"
                desc="内容使用、更新节奏、投稿方式和版权处理，都在这里说清楚。"
              />
            </div>
            <div className="lg:col-span-8">
              <FaqAccordion items={faqs} />
            </div>
          </div>
        </section>

        <section id="submit" className="bg-foreground-950 scroll-mt-24">
          <div className="container-page py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <span className="section-eyebrow text-accent-400">Submit & Contact</span>
                <h2 className="mt-3 text-2xl md:text-4xl text-background-50 text-balance">
                  投稿、反馈或合作，从这里开始
                </h2>
                <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed">
                  把你想分享的表情包、段子、神回复、梗图或二创视频告诉我们。也可以用来反馈内容问题或商谈合作，我们都会看。
                </p>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center gap-3 text-sm text-background-200">
                    <span className="w-9 h-9 rounded-md bg-background-50/10 flex items-center justify-center text-accent-400">
                      <i className="ri-mail-line"></i>
                    </span>
                    <a href={`mailto:${site.email}`} className="hover:text-background-50 transition-colors">
                      {site.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-background-200">
                    <span className="w-9 h-9 rounded-md bg-background-50/10 flex items-center justify-center text-accent-400">
                      <i className="ri-time-line"></i>
                    </span>
                    {site.updateTime}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-background-200">
                    <span className="w-9 h-9 rounded-md bg-background-50/10 flex items-center justify-center text-accent-400">
                      <i className="ri-reply-line"></i>
                    </span>
                    三个工作日内回复，采纳内容会标注署名
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <SubmitForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}